<template>
    <div class="segment-rule-description">
        <div class="rule-text-and-icons">
            <pendo-app-icon
                v-if="rule.id === 'application'"
                aria-hidden="true"
                :app="appForRule" />
            <pendo-icon
                v-if="iconType"
                aria-hidden="true"
                size="14"
                :type="iconType" />
            <pendo-icon
                v-if="usesEventProperty"
                aria-hidden="true"
                size="14"
                type="event-property" />
            <span class="rule-text">
                {{ rule.text }}
                <span
                    v-if="rule.entityRoute && rule.entityDeleted"
                    class="deleted-segment-name">
                    {{ rule.entityName }}
                </span>
                <pendo-router-link
                    v-else-if="rule.entityRoute"
                    :to="rule.entityRoute"
                    :target="openLinksInNewTab ? '_blank' : undefined"
                    @click="$emit('link-click', rule.entityRoute)">
                    {{ rule.entityName }}
                </pendo-router-link>
            </span>
        </div>
        <div
            v-if="rule.entityDeleted"
            class="deleted-segment-banner">
            <pendo-alert type="warning">
                {{
                    $t(
                        'segmentRuleDescription.deletedEntity.The nested segment has been deleted. Edit the segment to include a valid segment reference.',
                    )
                }}
            </pendo-alert>
        </div>
        <div
            v-if="childSegment"
            class="child-segment-rules">
            <segment-rules-list
                :segment="childSegment"
                :rules="childSegment.definition.filters"
                :show-title="false"
                :show-anonymous-visitor-data="showAnonymousVisitorData" />
        </div>
    </div>
</template>

<script>
import { PendoAlert, PendoIcon, PendoAppIcon } from '@pendo/components';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { mapGetters } from 'vuex';

export default {
    name: 'SegmentRuleDescription',
    components: {
        PendoAlert,
        PendoIcon,
        PendoAppIcon,
        PendoRouterLink,
    },
    props: {
        rule: {
            type: Object,
            default: () => ({}),
        },
        iconType: {
            type: String,
            default: undefined,
        },
        openLinksInNewTab: {
            type: Boolean,
            default: false,
        },
        usesEventProperty: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['link-click'],
    computed: {
        ...mapGetters('segments', ['segmentById']),
        ...mapGetters('apps', ['getAppById']),
        ...mapGetters({ getFeatureFlag: 'subscription/getFeatureFlag' }),
        childSegment () {
            const { segmentId } = this.rule;

            return segmentId ? this.segmentById(segmentId) : null;
        },
        appForRule () {
            return this.getAppById(this.rule.appId) || {};
        },
        showAnonymousVisitorData () {
            return this.getFeatureFlag('showAnonymous');
        },
    },
    beforeCreate () {
        // https://v2.vuejs.org/v2/guide/components-edge-cases.html?redirect=true#Circular-References-Between-Components
        this.$options.components.SegmentRulesList = require('./segment-rules-list.vue').default;
    },
};
</script>

<style scoped lang="less">
.rule-text-and-icons {
    display: flex;
    align-items: top;
    padding: 4px 0;

    .pendo-icon {
        min-width: 14px;
        margin-top: 3px;
    }

    .rule-text {
        padding-left: 10px;
        max-width: 50vw;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: break-word;
        text-overflow: ellipsis;
    }
}

.child-segment-rules,
.deleted-segment-banner {
    padding: 5px 0 0 25px;
}

.deleted-segment-name {
    font-weight: bold;
}
</style>
